import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/create-your-scent", label: "Create Your Scent" },
  { to: "/experience", label: "Candle Experience" },
  { to: "/favors", label: "Custom Favors" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Nav = () => {
  const { count, openCart } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <header data-testid="main-nav" className="sticky top-0 z-50 border-b border-line bg-ivory/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12 lg:px-20">
          <button data-testid="mobile-menu-button" className="lg:hidden p-2 -ml-2" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={20} strokeWidth={1.5} />
          </button>
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/assets/logo.png" alt="Merari Candle Company" className="h-9 w-auto object-contain" />
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-[10px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 hover:text-ink ${isActive ? "text-ink underline underline-offset-8 decoration-1" : "text-stone2"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-3">
            <button data-testid="nav-search-icon" onClick={() => navigate("/shop")} className="p-2 hover:opacity-60 transition-opacity" aria-label="Search">
              <Search size={18} strokeWidth={1.5} />
            </button>
            <button data-testid="nav-account-icon" onClick={() => navigate("/admin")} className="p-2 hover:opacity-60 transition-opacity" aria-label="Account">
              <User size={18} strokeWidth={1.5} />
            </button>
            <button data-testid="cart-drawer-toggle" onClick={openCart} className="relative p-2 hover:opacity-60 transition-opacity" aria-label="Open cart">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center bg-ink text-ivory text-[9px] font-medium">{count}</span>
              )}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {menuOpen && (
          <motion.div data-testid="mobile-menu" className="fixed inset-0 z-[60] bg-ivory flex flex-col"
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-line">
              <img src="/assets/logo.png" alt="Merari Candle Company" className="h-9 w-auto object-contain" />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="p-2" aria-label="Close menu">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}>
                  <NavLink to={l.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block border-b border-line py-4 font-serif text-3xl font-light tracking-tight">
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
